import { Check, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useId } from 'react';
import { SKIP_VALUE } from 'src/lib/constants.ts';
import { EASE_STANDARD } from 'src/lib/motion.ts';

const SCALE_LABELS: Record<number, string> = {
  1: 'Trifft nicht zu',
  2: 'Trifft eher nicht zu',
  3: 'Neutral',
  4: 'Trifft eher zu',
  5: 'Trifft zu'
};

const SCALE = [1, 2, 3, 4, 5];

export function LikertScale({
  value,
  onPick,
  legendOpen,
  onToggleLegend
}: {
  value?: number;
  onPick: (n: number) => void;
  legendOpen: boolean;
  onToggleLegend: () => void;
}) {
  const skipped = value === SKIP_VALUE;
  const legendId = useId();
  const prefersReduced = useReducedMotion();

  return (
    <div className="mt-8">
      {/* Dezenter Toggle für die ausformulierte Voll-Legende. */}
      <button
        type="button"
        aria-expanded={legendOpen}
        aria-controls={legendId}
        onClick={onToggleLegend}
        className="inline-flex items-center gap-1.5 text-caption text-zeb-gray-500 transition-colors hover:text-zeb-gray-700">
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 motion-reduce:transition-none ${
            legendOpen ? 'rotate-180' : ''
          }`}
        />
        Skala erklären
      </button>

      <AnimatePresence initial={false}>
        {legendOpen && (
          <motion.div
            id={legendId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={prefersReduced ? { duration: 0 } : { duration: 0.22, ease: EASE_STANDARD }}
            className="overflow-hidden">
            <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-caption text-zeb-gray-600">
              {SCALE.map((n) => (
                <div key={n} className="flex items-baseline gap-1.5">
                  <dt className="font-semibold text-zeb-tertiary tabular-nums">{n}</dt>
                  <dd>{SCALE_LABELS[n]}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-6 text-sm font-medium text-zeb-tertiary">Wählen Sie aus:</div>

      <div
        role="radiogroup"
        aria-label="Antwort von 1 (Trifft nicht zu) bis 5 (Trifft zu)"
        className="mt-3 grid grid-cols-5 gap-2 sm:gap-3">
        {SCALE.map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${n} von 5 – ${SCALE_LABELS[n]}`}
              onClick={() => onPick(n)}
              className={`group relative h-20 sm:h-24 rounded-md border transition-all duration-150 hover:-translate-y-px hover:shadow-zeb-xs active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none ${
                selected
                  ? 'border-zeb-primary bg-zeb-info-surface ring-2 ring-zeb-primary/30'
                  : 'border-zeb-muted bg-white hover:border-zeb-gray-300 hover:bg-zeb-surface'
              }`}>
              <div
                className={`text-2xl sm:text-3xl tabular-nums ${
                  selected ? 'text-zeb-primary' : 'text-zeb-tertiary'
                }`}
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em'
                }}>
                {n}
              </div>
            </button>
          );
        })}
      </div>

      {/* Permanente Mini-Anker — das Safety-Net, nie einklappbar. */}
      <div className="mt-2 flex justify-between text-caption text-zeb-gray-500">
        <span>Trifft nicht zu</span>
        <span>Trifft zu</span>
      </div>

      <div className="mt-6 flex justify-center">
        <button
          type="button"
          aria-pressed={skipped}
          onClick={() => onPick(SKIP_VALUE)}
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition active:scale-[0.98] motion-reduce:transform-none ${
            skipped
              ? 'border-zeb-primary bg-zeb-info-surface text-zeb-primary'
              : 'border-zeb-muted bg-white text-zeb-gray-600 hover:border-zeb-gray-300 hover:bg-zeb-surface'
          }`}>
          {skipped && <Check className="h-3.5 w-3.5" />}
          Keine Angabe
        </button>
      </div>
    </div>
  );
}
